import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const navLinks = [
  { href: "/", label: "Start" },
  { href: "/leistungen", label: "Leistungen" },
  { href: "/ueber-auralia", label: "Über Mich" },
  { href: "/faq", label: "FAQ" },
  { href: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050510]/90 backdrop-blur-md border-b border-[#1A1A2E]"
          : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo">
          <div
            className="text-[#C9A84C] font-light leading-none"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            <span className="text-xl tracking-[0.2em]">AURALIA</span>
            <span className="block text-[0.58rem] tracking-[0.38em] text-[#C9A84C]/70 mt-0.5">
              HEALING
            </span>
          </div>
        </Link>

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-xs uppercase tracking-[0.22em] transition-colors duration-300 ${
                location.pathname === link.href
                  ? "text-[#C9A84C]"
                  : "text-[#9A9A9A] hover:text-[#F0EDE8]"
              }`}
              data-testid={`nav-${link.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/leistungen"
            className="btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.18em] px-6 py-2.5 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300"
            data-testid="nav-buchen-btn"
          >
            Jetzt buchen
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-[#F0EDE8] p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menü öffnen"
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-[#050510]/95 backdrop-blur-md border-b border-[#1A1A2E] overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-xs uppercase tracking-[0.22em] transition-colors ${
                    location.pathname === link.href ? "text-[#C9A84C]" : "text-[#9A9A9A]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/leistungen"
                className="border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.18em] px-6 py-3 text-center mt-2"
              >
                Jetzt buchen
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
